import React, { useState } from 'react';
import { Button, Dropdown, Menu, Checkbox } from 'antd';
import { DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { getCommonTimezones, formatTimezoneOffset } from '../utils/timezoneUtils';
import './TimezoneSelector.css';

const TimezoneSelector = ({ darkMode, selectedTimezone, onTimezoneChange }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const commonTimezones = getCommonTimezones();

  const handleVisibleChange = (visible) => {
    setDropdownVisible(visible);
  };

  const handleTimezoneSelect = ({ key }) => {
    const selected = commonTimezones.find(tz => tz.code === key);
    if (selected) {
      onTimezoneChange(selected);
    }
    setDropdownVisible(false);
  };

  const getTimezoneDisplay = (timezone) => {
    if (!timezone) return 'UTC';
    
    if (timezone.code === 'UTC') return 'UTC';
    if (timezone.code === 'Exchange') return 'Exchange';
    
    // Use current offset that accounts for DST if available
    const offset = timezone.currentOffset !== undefined ? timezone.currentOffset : timezone.offset;
    
    // Use the utility function to format the offset with proper sign
    const formattedOffset = formatTimezoneOffset(offset);
    
    return `(UTC${formattedOffset}) ${timezone.name}`;
  };

  const getMenuItemStyle = (timezone) => {
    const isSelected = timezone.code === selectedTimezone?.code;
    return {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: isSelected ? 'rgba(38, 166, 154, 0.2)' : 'transparent',
      fontWeight: isSelected ? 'bold' : 'normal'
    };
  };

  const renderTimezoneItem = (timezone) => {
    const isSelected = timezone.code === selectedTimezone?.code;
    const displayText = getTimezoneDisplay(timezone);
    
    // Split the display text to separate the UTC part for alignment
    let utcPart = "";
    let namePart = displayText;
    
    if (timezone.code !== 'UTC' && timezone.code !== 'Exchange') {
      const match = displayText.match(/(\(UTC[+-]\d+(?::\d+)?\))\s(.+)/);
      if (match) {
        utcPart = match[1];
        namePart = match[2];
      }
    }
    
    return (
      <div style={getMenuItemStyle(timezone)} className="ntc-timezone-item-content">
        <div className="ntc-timezone-item-text">
          {utcPart ? (
            <>
              <span className="ntc-timezone-utc-part">{utcPart}</span>
              <span className="ntc-timezone-name-part">{namePart}</span>
            </>
          ) : (
            <span>{displayText}</span>
          )}
          
          {timezone.isDST && (
            <span className="ntc-dst-indicator"> DST</span>
          )}
        </div>
        {isSelected && (
          <Checkbox checked={true} style={{ pointerEvents: 'none' }} />
        )}
      </div>
    );
  };

  const menu = (
    <Menu 
      onClick={handleTimezoneSelect}
      selectedKeys={[selectedTimezone?.code || 'UTC']}
      className={`ntc-timezone-menu ${darkMode ? 'dark' : 'light'}`}
    >
      {commonTimezones.map(timezone => (
        <Menu.Item key={timezone.code} className="ntc-timezone-item">
          {renderTimezoneItem(timezone)}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className={`ntc-timezone-selector ${darkMode ? 'dark' : 'light'}`}>
      <Dropdown
        overlay={menu}
        trigger={['click']}
        open={dropdownVisible}
        onOpenChange={handleVisibleChange}
      >
        <Button 
          type="text" 
          className="ntc-timezone-button"
          icon={<GlobalOutlined />}
        >
          <span className="ntc-timezone-display">
            {getTimezoneDisplay(selectedTimezone)}
            {selectedTimezone?.isDST && (
              <span className="ntc-dst-indicator-button"> DST</span>
            )}
          </span>
          <DownOutlined className="ntc-timezone-dropdown-icon" />
        </Button>
      </Dropdown>
    </div>
  );
};

export default TimezoneSelector; 